import { useEffect, useRef, useState } from 'react'
import type { Editor } from '@tiptap/core'
import { ArrowUp, AtSign, FileText, Loader2, Paperclip, Pencil, Trash2, X } from 'lucide-react'
import { api } from '../lib/api'
import type { Actor, Attachment, Comment, ProjectMember } from '../lib/types'
import { formatBytes, formatRelative } from '../lib/format'
import { realtime, type RealtimeEvent } from '../lib/realtime'
import { errorText, useToast } from './Toaster'
import { useConfirm } from './Confirm'
import { RichEditor } from './RichEditor'
import { AttachmentList } from './AttachmentList'
import { FileGlyph } from './FileGlyph'
import { Lightbox } from './Lightbox'
import { Avatar, Button, IconButton, RichView, Spinner } from '../ds'

// Локальное состояние выбранного, но ещё не отправленного файла.
interface PendingAttachment {
  localId: string
  name: string
  size: number
  isImage: boolean
  status: 'uploading' | 'done' | 'error'
  previewUrl?: string
  attachment?: Attachment
}

// Пустой ли rich-html (только теги/пробелы) — чтобы не отправлять пустой комментарий.
function isBlankHtml(html: string): boolean {
  return !html.replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim()
}

function upsert(list: Comment[], comment: Comment): Comment[] {
  const index = list.findIndex((item) => item.id === comment.id)
  if (index === -1) {
    return [...list, comment]
  }
  const next = list.slice()
  next[index] = comment
  return next
}

export function Comments({
  postId,
  projectId,
  actor,
  members,
  canModerate
}: {
  postId: string
  projectId: string
  actor: Actor
  members: ProjectMember[]
  canModerate: boolean
}) {
  const { notify } = useToast()
  const confirm = useConfirm()
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(true)
  const [draft, setDraft] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editHtml, setEditHtml] = useState('')
  const [pending, setPending] = useState<PendingAttachment[]>([])
  const [dragOver, setDragOver] = useState(false)
  const [lightbox, setLightbox] = useState<string | null>(null)
  const composerEditor = useRef<Editor | null>(null)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const activeMembers = members.filter((member) => member.status === 'active')
  const readyAttachments = pending.filter((item) => item.status === 'done' && item.attachment)
  const uploadingCount = pending.filter((item) => item.status === 'uploading').length
  const canSend = !submitting && uploadingCount === 0 && (!isBlankHtml(draft) || readyAttachments.length > 0)

  // Кнопка «@» — вставляет символ в редактор, что открывает выпадашку упоминаний.
  function insertMention() {
    composerEditor.current?.chain().focus().insertContent('@').run()
  }

  useEffect(() => {
    let alive = true
    setLoading(true)
    api
      .listPostComments(postId)
      .then((res) => {
        if (alive) setComments(res.comments)
      })
      .catch((err) => {
        if (alive) notify(errorText(err), 'error')
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId])

  // Realtime: живые комментарии в открытом посте. Подписка ref-count на проект,
  // фильтруем события по postId. Дедуп по id гасит эхо собственной отправки.
  useEffect(() => {
    realtime.subscribe(projectId)
    const off = realtime.on((event: RealtimeEvent) => {
      if (event.postId !== postId) return
      if (event.type === 'comment:created' || event.type === 'comment:updated') {
        setComments((prev) => upsert(prev, event.comment as Comment))
      } else if (event.type === 'comment:deleted') {
        setComments((prev) => prev.filter((item) => item.id !== event.commentId))
      }
    })
    return () => {
      realtime.unsubscribe(projectId)
      off()
    }
  }, [projectId, postId])

  // Загрузка файлов в композер (pending): сразу шлём в S3, статус — в превью.
  async function uploadFiles(files: File[]) {
    for (const file of files) {
      const localId = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`
      const isImage = file.type.startsWith('image/')
      const previewUrl = isImage ? URL.createObjectURL(file) : undefined
      setPending((prev) => [...prev, { localId, name: file.name, size: file.size, isImage, status: 'uploading', previewUrl }])
      try {
        const res = await api.uploadAttachment(postId, file)
        setPending((prev) =>
          prev.map((item) => (item.localId === localId ? { ...item, status: 'done', attachment: res.attachment } : item))
        )
      } catch (err) {
        if (previewUrl) URL.revokeObjectURL(previewUrl)
        setPending((prev) => prev.filter((item) => item.localId !== localId))
        notify(errorText(err), 'error')
      }
    }
  }

  function removePending(localId: string) {
    setPending((prev) => {
      const item = prev.find((entry) => entry.localId === localId)
      if (item?.previewUrl) URL.revokeObjectURL(item.previewUrl)
      // Файл уже в S3 (pending) — удаляем fire-and-forget.
      if (item?.attachment) api.deleteAttachment(item.attachment.id).catch(() => {})
      return prev.filter((entry) => entry.localId !== localId)
    })
  }

  function clearPending() {
    setPending((prev) => {
      for (const item of prev) {
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl)
      }
      return []
    })
  }

  async function submit() {
    if (!canSend) return
    setSubmitting(true)
    try {
      const attachmentIds = readyAttachments.map((item) => item.attachment!.id)
      const res = await api.createComment(postId, { html: draft, attachmentIds })
      setComments((prev) => upsert(prev, res.comment))
      setDraft('')
      clearPending()
    } catch (err) {
      notify(errorText(err), 'error')
    } finally {
      setSubmitting(false)
    }
  }

  async function saveEdit(commentId: string) {
    if (isBlankHtml(editHtml)) return
    try {
      const res = await api.updateComment(commentId, { html: editHtml })
      setComments((prev) => upsert(prev, res.comment))
      setEditingId(null)
      setEditHtml('')
    } catch (err) {
      notify(errorText(err), 'error')
    }
  }

  async function remove(comment: Comment) {
    if (!comment.id) return
    const ok = await confirm({
      title: 'Удалить комментарий?',
      message: 'Это действие необратимо.'
    })
    if (!ok) return
    try {
      await api.deleteComment(comment.id)
      setComments((prev) => prev.filter((item) => item.id !== comment.id))
    } catch (err) {
      notify(errorText(err), 'error')
    }
  }

  return (
    <section className="comments">
      <div className="comments-title">
        Обсуждение
        {comments.length > 0 && <span className="comments-count">{comments.length}</span>}
      </div>

      {loading ? (
        <div className="comments-loading">
          <Spinner size={20} />
        </div>
      ) : (
        <div className="comment-list">
          {comments.length === 0 && <div className="comments-empty">Пока нет комментариев. Начните обсуждение.</div>}
          {comments.map((comment) => {
            const canEdit = comment.authorEmail === actor.email
            const canDelete = canEdit || canModerate
            const isEditing = editingId === comment.id
            return (
              <article key={comment.id} className="comment">
                <div className="comment-rail">
                  <Avatar email={comment.authorEmail} size={26} />
                </div>
                <div className="comment-main">
                  <div className="comment-head">
                    <span className="comment-author">
                      {comment.authorEmail === actor.email ? 'Вы' : comment.authorEmail}
                    </span>
                    <span className="comment-time">
                      {formatRelative(comment.createdAt)}
                      {comment.editedAt ? ' · изменён' : ''}
                    </span>
                    {!isEditing && (
                      <span className="comment-actions">
                        {canEdit && (
                          <IconButton
                            label="Редактировать"
                            onClick={() => {
                              setEditingId(comment.id)
                              setEditHtml(comment.html)
                            }}
                          >
                            <Pencil size={14} />
                          </IconButton>
                        )}
                        {canDelete && (
                          <IconButton label="Удалить" tone="danger" onClick={() => remove(comment)}>
                            <Trash2 size={14} />
                          </IconButton>
                        )}
                      </span>
                    )}
                  </div>
                  {isEditing ? (
                    <div className="comment-edit">
                      <RichEditor
                        value={editHtml}
                        onChange={setEditHtml}
                        members={activeMembers}
                        toolbar={false}
                        bubble
                        onSubmit={() => comment.id && saveEdit(comment.id)}
                        placeholder="Изменить комментарий…"
                      />
                      <div className="comment-edit-actions">
                        <Button size="sm" onClick={() => comment.id && saveEdit(comment.id)} disabled={isBlankHtml(editHtml)}>
                          Сохранить
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingId(null)
                            setEditHtml('')
                          }}
                        >
                          Отмена
                        </Button>
                      </div>
                    </div>
                  ) : (
                    comment.html && !isBlankHtml(comment.html) && <RichView className="comment-body" html={comment.html} />
                  )}
                  <AttachmentList attachments={comment.attachments} onOpenImage={setLightbox} />
                </div>
              </article>
            )
          })}
        </div>
      )}

      {/* Композер: аватар + поле ввода с форматированием/вложениями + отправка. */}
      <div className="comment-composer">
        <Avatar email={actor.email} size={26} />
        <div
          className={['comment-composer-field', dragOver ? 'is-dragover' : ''].filter(Boolean).join(' ')}
          onDragOver={(event) => {
            event.preventDefault()
            if (!dragOver) setDragOver(true)
          }}
          onDragLeave={(event) => {
            if (event.currentTarget === event.target) setDragOver(false)
          }}
          onDrop={(event) => {
            event.preventDefault()
            setDragOver(false)
            const files = Array.from(event.dataTransfer?.files ?? [])
            if (files.length) void uploadFiles(files)
          }}
          onPaste={(event) => {
            const files = Array.from(event.clipboardData?.files ?? [])
            if (files.length) {
              event.preventDefault()
              void uploadFiles(files)
            }
          }}
        >
          <div className="comment-composer-row">
            <div className="comment-composer-input">
              <RichEditor
                value={draft}
                onChange={setDraft}
                members={activeMembers}
                toolbar={false}
                bubble
                onReady={(editor) => {
                  composerEditor.current = editor
                }}
                onSubmit={submit}
                placeholder="Добавить комментарий…"
              />
            </div>
            <div className="comment-composer-tools">
              <button type="button" className="comment-tool" aria-label="Прикрепить файл" onClick={() => fileInputRef.current?.click()}>
                <Paperclip size={16} />
              </button>
              <button type="button" className="comment-tool" aria-label="Упомянуть" onClick={insertMention}>
                <AtSign size={16} />
              </button>
              <button type="button" className="comment-send" aria-label="Отправить" onClick={submit} disabled={!canSend}>
                <ArrowUp size={15} />
              </button>
            </div>
          </div>

          {pending.length > 0 && (
            <div className="composer-attachments">
              {pending.map((item) => (
                <div className={`pending-attachment ${item.status}`} key={item.localId}>
                  {item.isImage && item.previewUrl ? (
                    <img className="pending-thumb" src={item.previewUrl} alt={item.name} />
                  ) : (
                    <span className="pending-icon">
                      {item.attachment ? <FileGlyph attachment={item.attachment} /> : <FileText size={18} />}
                    </span>
                  )}
                  <span className="pending-meta">
                    <span className="pending-name">{item.name}</span>
                    <span className="pending-size">{item.status === 'uploading' ? 'загрузка…' : formatBytes(item.size)}</span>
                  </span>
                  {item.status === 'uploading' ? (
                    <Loader2 size={14} className="pending-spin spin" />
                  ) : (
                    <button type="button" className="pending-remove" aria-label="Убрать" onClick={() => removePending(item.localId)}>
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          hidden
          onChange={(event) => {
            const files = Array.from(event.target.files ?? [])
            if (files.length) void uploadFiles(files)
            event.target.value = ''
          }}
        />
      </div>

      <Lightbox url={lightbox} onClose={() => setLightbox(null)} />
    </section>
  )
}
